import React, { useEffect } from 'react';
import {
  FaBell,
  FaBookOpen,
  FaCalendarAlt,
  FaChair,
  FaClock,
  FaEdit,
  FaEnvelope,
  FaGraduationCap,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhoneAlt,
  FaTimes,
  FaUser,
  FaWallet,
} from 'react-icons/fa';
import '../styles/pages/student-details.css';

const EMPTY = '\u2014';

const hasValue = value => (
  value !== null
  && value !== undefined
  && String(value).trim() !== ''
  && String(value).trim() !== '-'
  && !String(value).includes('\uFFFD')
);

const pick = (...values) => values.find(hasValue);

const displayText = (...values) => {
  const value = pick(...values);
  return value === undefined ? EMPTY : String(value).trim();
};

const formatDate = (...values) => {
  const value = pick(...values);
  if (value === undefined) return EMPTY;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMoney = value => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return EMPTY;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const joinDisplay = (...values) => {
  const parts = values.filter(value => value && value !== EMPTY);
  return parts.length ? parts.join(' \u00b7 ') : EMPTY;
};

const formatPhone = value => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return EMPTY;
  if (digits.length > 10) return `+${digits.slice(0, digits.length - 10)} ${digits.slice(-10)}`;
  return `+91 ${digits}`;
};

const titleCase = value => {
  const text = String(value || '').trim();
  if (!text) return EMPTY;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

function DetailItem({ label, value, children, className = '' }) {
  return (
    <div className={`student-detail-item ${className}`.trim()}>
      <span className="student-detail-label">{label}</span>
      <div className="student-detail-value">{children ?? value ?? EMPTY}</div>
    </div>
  );
}

function DetailSection({ icon: Icon, title, subtitle, children, className = '' }) {
  return (
    <section className={`student-details-section ${className}`.trim()}>
      <div className="student-details-section-head">
        <span className="student-details-section-icon" aria-hidden="true"><Icon /></span>
        <div>
          <h3>{title}</h3>
          {subtitle && <p>{subtitle}</p>}
        </div>
      </div>
      <div className="student-details-fields">{children}</div>
    </section>
  );
}

export default function StudentDetailsModal({ student, onClose, onEdit, onPay, onNotify }) {
  const isOpen = Boolean(student);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose?.();
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!student) return null;

  const fullName = displayText(student.name, student.full_name);
  const studentCode = displayText(
    student.student_id_code,
    student.studentId,
    student.student_id,
    student.userid,
    student.student_uuid,
    student.id,
    student._id,
  );
  const email = displayText(student.email);
  const phone = formatPhone(pick(student.phone, student.contact_no, student.mobile));
  const library = displayText(
    student.library_name_display,
    student.library_name,
    student.library,
    student.library_plan_status,
  );
  const seatNumber = displayText(
    student.seat_number_display,
    student.seatnumber,
    student.seat_number,
    student.seat,
  );
  const room = displayText(student.room_name_display, student.room_name, student.roomname);
  const categoryText = displayText(
    student.seat_category_display,
    student.seat_category,
    student.seatcategory,
  );
  const seatCategory = categoryText === EMPTY
    ? EMPTY
    : /non/i.test(categoryText) ? 'Non-AC' : 'AC';
  const planTypeRaw = String(pick(student.plan_type_display, student.plan_type, 'general')).toLowerCase();
  const planType = planTypeRaw === 'reserved' ? 'Reserved' : 'General';
  const subscription = displayText(
    student.subscription_display,
    student.subscription_type,
    student.subscription,
  );
  const planName = displayText(
    student.plan_name,
    student.planName,
    student.membership_type,
    subscription,
  );
  const admissionDate = formatDate(student.admission_date, student.admissionDate, student.joined_date, student.joined);
  const startDate = formatDate(student.subscription_start, student.subscriptionstart, student.start_date);
  const endDate = formatDate(student.subscription_end, student.subscriptionend, student.end_date);
  const registeredOn = formatDate(student.created_at, student.createdat);
  const planAmount = formatMoney(pick(
    student.plan_amount,
    student.subscription_plan_amount,
    student.planamount,
  ));
  const dueValue = Number(pick(student.amount_due, student.total_amount_due, student.due) ?? 0);
  const due = Number.isFinite(dueValue) ? dueValue : 0;
  const paidValue = Number(pick(
    student.paid_amount,
    student.total_paid_amount,
    student.subscription_paid_amount,
  ) ?? 0);
  const paid = Number.isFinite(paidValue) ? paidValue : 0;
  const upgradeFeeValue = pick(
    student.seat_upgrade_fee_amount,
    student.seat_upgrade_paid,
    student.seat_upgrade_due,
  );
  const hasUpgrade = hasValue(upgradeFeeValue) || hasValue(student.seat_upgrade_id);
  const address = displayText(student.address);
  const addedBy = displayText(student.added_by, student.addedby);
  const addedByEmail = displayText(student.added_by_email, student.addedbyemail);
  const rawDays = pick(student.days_remaining, student.daysremaining);
  const daysNumber = Number(rawDays);
  const hasDays = hasValue(rawDays) && Number.isFinite(daysNumber);
  const isExpired = Boolean(student.is_expired) || (hasDays && daysNumber < 0);
  const periodStart = new Date(pick(
    student.subscription_start,
    student.subscriptionstart,
    student.start_date,
  )).getTime();
  const periodEnd = new Date(pick(
    student.subscription_end,
    student.subscriptionend,
    student.end_date,
  )).getTime();
  const hasPeriod = Number.isFinite(periodStart) && Number.isFinite(periodEnd) && periodEnd > periodStart;
  const periodProgress = hasPeriod
    ? Math.min(100, Math.max(0, Math.round(((Date.now() - periodStart) / (periodEnd - periodStart)) * 100)))
    : 0;
  const periodLabel = isExpired ? 'Period ended' : `${periodProgress}% elapsed`;
  const daysLabel = !hasDays
    ? EMPTY
    : isExpired
      ? 'Expired'
      : daysNumber === 0
        ? 'Expires today'
        : `${daysNumber} days`;
  const daysNote = !hasDays
    ? 'No active subscription'
    : isExpired
      ? `${Math.abs(daysNumber)} days ago`
      : 'remaining';
  const statusRaw = String(pick(student.status, student.status_normalized, 'active')).toLowerCase();
  const statusLabel = titleCase(statusRaw);
  const statusTone = statusRaw === 'active' ? 'active' : 'inactive';
  const paymentLabel = due > 0 ? 'Outstanding' : 'Fully paid';
  const paymentTone = due > 0 ? 'due' : 'paid';
  const initials = fullName === EMPTY
    ? 'S'
    : fullName.split(/\s+/).filter(Boolean).map(part => part[0]).join('').slice(0, 2).toUpperCase();

  const handleEdit = () => {
    onClose?.();
    onEdit?.(student);
  };

  const handlePay = () => {
    onClose?.();
    onPay?.(student);
  };

  const summaryItems = [
    {
      icon: FaLayerGroup,
      label: 'Current plan',
      value: subscription,
      note: joinDisplay(`${planType} plan`, planAmount !== EMPTY ? planAmount : ''),
      tone: '',
    },
    {
      icon: FaClock,
      label: 'Validity',
      value: daysLabel,
      note: hasDays ? `${isExpired ? 'Ended' : 'Ends'} ${endDate}` : daysNote,
      tone: isExpired ? 'danger' : 'success',
    },
    {
      icon: FaWallet,
      label: 'Outstanding',
      value: due > 0 ? formatMoney(due) : 'Fully paid',
      note: due > 0 ? `${formatMoney(paid)} paid` : 'No balance due',
      tone: due > 0 ? 'danger' : 'success',
    },
    {
      icon: FaChair,
      label: 'Seat',
      value: seatNumber,
      note: joinDisplay(room !== EMPTY ? room : '', seatCategory !== EMPTY ? `${seatCategory} seat` : ''),
      tone: planType === 'Reserved' ? 'warning' : '',
    },
  ];

  return (
    <div
      className="student-details-overlay"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <section
        className="student-details-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="student-details-title"
      >
        <header className="student-details-header">
          <div className="student-details-header-art" aria-hidden="true"><FaGraduationCap /></div>
          <div className="student-details-header-top">
            <div className="student-details-identity">
              <div className="student-details-avatar-wrap" aria-hidden="true">
                <div className="student-details-avatar">{initials}</div>
                <span className={`student-details-avatar-status ${statusTone}`} />
              </div>
              <div className="student-details-identity-copy">
                <div className="student-details-kicker">
                  <span className={`student-details-status ${statusTone}`}>{statusLabel}</span>
                  <span className="student-details-code">{studentCode}</span>
                </div>
                <h2 id="student-details-title">{fullName}</h2>
                <p className="student-details-role">Student</p>
              </div>
            </div>
            <button
              type="button"
              className="student-details-close"
              onClick={onClose}
              aria-label="Close student details"
            >
              <FaTimes aria-hidden="true" />
            </button>
          </div>
          <div className="student-details-contact">
            <div className="student-details-meta">
              <span><FaEnvelope aria-hidden="true" />{email}</span>
              <span><FaPhoneAlt aria-hidden="true" />{phone}</span>
            </div>
            <div className="student-details-tags">
              <span><FaBookOpen aria-hidden="true" />{library}</span>
              <span><FaChair aria-hidden="true" />Seat {seatNumber}</span>
            </div>
          </div>
        </header>

        <div className="student-details-body">
          <div className="student-details-summary">
            {summaryItems.map(item => {
              const Icon = item.icon;
              return (
                <div className={`student-details-summary-card ${item.tone}`.trim()} key={item.label}>
                  <span className="student-details-summary-icon" aria-hidden="true"><Icon /></span>
                  <div>
                    <span>{item.label}</span>
                    <b>{item.value}</b>
                    <small>{item.note}</small>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="student-details-grid">
            <DetailSection
              icon={FaUser}
              title="Personal details"
              subtitle="Contact information and student identity"
            >
              <DetailItem label="Full name" value={fullName} />
              <DetailItem label="Student ID" value={studentCode} />
              <DetailItem label="Email address" className="student-detail-item-span-2" value={email} />
              <DetailItem label="Phone number" value={phone} />
              <DetailItem label="Admission date" value={admissionDate} />
              <DetailItem label="Record status" className="student-detail-item-span-2" value={statusLabel} />
            </DetailSection>

            <DetailSection
              icon={FaCalendarAlt}
              title="Subscription & billing"
              subtitle="Plan details, validity, and charges"
            >
              {hasPeriod && (
                <div className={`student-details-period ${isExpired ? 'expired' : ''}`.trim()}>
                  <div className="student-details-period-head">
                    <span>Subscription period</span>
                    <b>{periodLabel}</b>
                  </div>
                  <div className="student-details-period-track">
                    <span className="student-details-period-fill" style={{ width: `${periodProgress}%` }} />
                  </div>
                  <div className="student-details-period-dates">
                    <span>
                      <small>Started</small>
                      <b>{startDate}</b>
                    </span>
                    <span>
                      <small>Ends</small>
                      <b>{endDate}</b>
                    </span>
                  </div>
                </div>
              )}
              <DetailItem label="Plan type" value={planType} />
              <DetailItem label="Billing cycle" value={subscription} />
              <DetailItem label="Plan name" value={planName} />
              <DetailItem label="Plan amount" value={planAmount} />
              <DetailItem label="Registered on" value={registeredOn} />
              {hasUpgrade && (
                <DetailItem
                  label="Seat upgrade fee"
                  value={formatMoney(upgradeFeeValue ?? 0)}
                />
              )}
            </DetailSection>

            <DetailSection
              icon={FaChair}
              title="Seat allocation"
              subtitle="Workspace and branch assignment"
            >
              <DetailItem label="Assigned seat" value={seatNumber} />
              <DetailItem label="Room" value={room} />
              <DetailItem label="Seat category" value={seatCategory} />
              <DetailItem label="Seat type" value={planType} />
              <DetailItem label="Library" className="student-detail-item-span-2" value={library} />
            </DetailSection>

            <DetailSection
              icon={FaMoneyBillWave}
              title="Account & audit"
              subtitle="Payment position and record metadata"
            >
              <DetailItem
                label="Payment status"
                className="student-detail-item-span-2"
                value={(
                  <span className={`student-details-payment ${paymentTone}`}>
                    <span aria-hidden="true" />
                    {paymentLabel}
                  </span>
                )}
              />
              <DetailItem label="Amount due" value={formatMoney(due)} />
              <DetailItem label="Amount paid" value={formatMoney(paid)} />
              <DetailItem label="Added by" className="student-detail-item-span-2" value={addedBy} />
              <DetailItem label="Added by email" className="student-detail-item-span-2" value={addedByEmail} />
            </DetailSection>
          </div>

          {address !== EMPTY && (
            <section className="student-details-section student-details-address">
              <div className="student-details-section-head">
                <span className="student-details-section-icon" aria-hidden="true"><FaMapMarkerAlt /></span>
                <div>
                  <h3>Address</h3>
                  <p>Residential address on record</p>
                </div>
              </div>
              <p className="student-details-address-text">{address}</p>
            </section>
          )}
        </div>

        <footer className="student-details-footer">
          <div className="student-details-footer-meta">
            <span className={`student-details-footer-dot ${paymentTone}`} aria-hidden="true" />
            <div>
              <b>{due > 0 ? `${formatMoney(due)} outstanding` : 'No outstanding balance'}</b>
              <small>{due > 0 ? 'Payment action is due for this student' : 'Account is fully settled'}</small>
            </div>
          </div>
          <div className="student-details-actions">
            {onNotify && (
              <button type="button" className="student-details-btn ghost" onClick={() => onNotify(student)}>
                <FaBell aria-hidden="true" />
                <span>Send reminder</span>
              </button>
            )}
            {onPay && due > 0 && (
              <button type="button" className="student-details-btn soft" onClick={handlePay}>
                <FaMoneyBillWave aria-hidden="true" />
                <span>Collect payment</span>
              </button>
            )}
            {onEdit && (
              <button type="button" className="student-details-btn primary" onClick={handleEdit}>
                <FaEdit aria-hidden="true" />
                <span>Edit profile</span>
              </button>
            )}
            <button type="button" className="student-details-btn ghost" onClick={onClose}>
              <FaTimes aria-hidden="true" />
              <span>Close</span>
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
